/* ============================================================
   PLATONIC — Cart drawer engine (Shopify AJAX cart).
   Replaces the prototype's localStorage cart. Renders /cart.js,
   mutates via /cart/add.js + /cart/change.js, and reflects Shopify's
   REAL totals + automatic discounts (no client-side discount math).
   Exposes window.PlatonicCart = { open, close, refresh, addVariant }.
   ============================================================ */
(function () {
  'use strict';

  const drawer = document.querySelector('[data-cart-drawer]');
  if (!drawer) return;
  const backdrop = document.querySelector('.drawer-backdrop');
  const body = drawer.querySelector('[data-cart-body]');
  const insertAnchor = drawer.querySelector('.drawer-pair') || drawer.querySelector('.drawer-visitor');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');
  const checkoutTotalEl = drawer.querySelector('[data-cart-checkout-total]');
  const META = window.PlatonicCartMeta || {};
  const R = window.routes || {};
  const addUrl = (R.cart_add_url || '/cart/add') + '.js';
  const changeUrl = (R.cart_change_url || '/cart/change') + '.js';
  const updateUrl = (R.cart_update_url || '/cart/update') + '.js';
  const cartJsUrl = '/cart.js';
  const MAX_QTY = 10; // per-line cap — premium: discourages resellers / card-testing
  let lastCart = null; // most recent cart object, so adds can compute absolute quantity

  // Show the exact amount (paise included) so the cart total matches checkout —
  // 2 decimals only when there's a fractional part, whole prices stay clean.
  const money = (cents) => {
    cents = Math.round(cents || 0);
    const hasPaise = cents % 100 !== 0;
    return '₹' + (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: hasPaise ? 2 : 0,
      maximumFractionDigits: 2,
    });
  };

  /* ---- Accessible status announcements (WCAG 4.1.3 Status Messages).
     One polite, visually-hidden live region voices add / quantity / removal, so
     screen-reader users get the feedback that silent DOM swaps otherwise hide.
     Fired only from user actions (addVariant / changeLine), never a passive refresh. ---- */
  let liveRegion = null;
  function announce(msg) {
    if (!msg) return;
    if (!liveRegion) {
      liveRegion = document.createElement('p');
      liveRegion.className = 'visually-hidden';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('role', 'status');
      document.body.appendChild(liveRegion);
    }
    // Clear first, then set on the next frame, so an identical repeat message
    // (e.g. adding the same item twice) is still re-announced.
    liveRegion.textContent = '';
    window.requestAnimationFrame(() => { liveRegion.textContent = msg; });
  }
  const itemWord = (n) => (n === 1 ? 'item' : 'items');

  /* ---- render the whole drawer from a Shopify cart object ---- */
  function render(cart) {
    // Ignore throttle/error responses (they have no items[]) — without this guard a
    // rapid +/+/+ burst makes one request come back as an error and flashes the empty cart.
    if (!cart || !Array.isArray(cart.items)) return;
    lastCart = cart; // cache for absolute-quantity adds
    // clear existing line nodes
    drawer.querySelectorAll('.drawer-line').forEach((n) => n.remove());

    (cart.items || []).forEach((item) => {
      const meta = META[item.handle];
      const line = document.createElement('div');
      line.className = 'drawer-line';
      line.dataset.key = item.key;
      if (meta) line.style.setProperty('--line-color', meta.dot);

      // Use the line item's own image (the variant image = clean bottle, same as
      // checkout) so the cart and checkout thumbnails always match; meta.img is a fallback.
      let img = (item.featured_image && item.featured_image.url) || item.image || (meta && meta.img) || '';
      // Speed audit 2026-11-04: /cart.js returns the ORIGINAL file (~160KB) — this runs on every
      // page while the cart holds an item. Ask the CDN for 320w (thumb shows at 104-144px; same
      // size the cart page uses via image_url: width: 320).
      if (img && !/[?&]width=/.test(img)) { try { const u = new URL(img, location.href); u.searchParams.set('width', '320'); img = u.toString(); } catch (e) {} }
      const name = (meta && meta.name) || item.product_title || item.title;
      const variant = meta ? 'Eau de Parfum' : (item.variant_title || '');

      const imgDiv = document.createElement('div');
      imgDiv.className = 'drawer-line-image';
      const imgEl = document.createElement('img');
      imgEl.src = img; imgEl.alt = ''; imgEl.loading = 'lazy';
      imgDiv.appendChild(imgEl);

      const mid = document.createElement('div');
      const nameP = document.createElement('p');
      nameP.className = 'drawer-line-name';
      nameP.textContent = name;
      const accent = document.createElement('span');
      accent.className = 'drawer-line-accent'; accent.setAttribute('aria-hidden', 'true');
      const varP = document.createElement('p');
      varP.className = 'drawer-line-variant';
      varP.textContent = variant;

      const actions = document.createElement('div');
      actions.className = 'drawer-line-actions';
      const qtyDiv = document.createElement('div');
      qtyDiv.className = 'drawer-line-qty';
      const minus = document.createElement('button');
      minus.type = 'button'; minus.setAttribute('data-qty-minus', ''); minus.setAttribute('aria-label', 'Decrease'); minus.textContent = '−';
      const qtySpan = document.createElement('span');
      qtySpan.textContent = String(item.quantity);
      const plus = document.createElement('button');
      plus.type = 'button'; plus.setAttribute('data-qty-plus', ''); plus.setAttribute('aria-label', 'Increase'); plus.textContent = '+';
      if (item.quantity >= MAX_QTY) { plus.disabled = true; plus.classList.add('is-max'); }
      qtyDiv.append(minus, qtySpan, plus);
      const remove = document.createElement('button');
      remove.type = 'button'; remove.className = 'drawer-line-remove'; remove.setAttribute('data-line-remove', ''); remove.textContent = 'Remove';
      actions.append(qtyDiv);
      if (item.quantity >= MAX_QTY) {
        const maxNote = document.createElement('span');
        maxNote.className = 'drawer-line-max'; maxNote.textContent = 'Max ' + MAX_QTY;
        actions.append(maxNote);
      }
      actions.append(remove);
      mid.append(nameP, accent, varP, actions);

      const priceP = document.createElement('p');
      priceP.className = 'drawer-line-price';
      priceP.textContent = money(item.original_line_price);

      line.append(imgDiv, mid, priceP);
      body.insertBefore(line, insertAnchor);
    });

    // totals — reflect Shopify's real discount, if any
    const original = cart.original_total_price || cart.total_price;
    const now = cart.total_price;
    const discounted = (cart.total_discount || 0) > 0;
    // Total shown plain (no strike-through); the saving is its own centred
    // statement above it, visible only once Shopify's discount has landed.
    if (subtotalEl) subtotalEl.textContent = money(now);
    if (checkoutTotalEl) checkoutTotalEl.textContent = money(now);
    const saved = drawer.querySelector('[data-cart-saved]');
    if (saved) {
      saved.hidden = !discounted;
      const amt = saved.querySelector('[data-cart-saved-amt]');
      if (amt) amt.textContent = money(cart.total_discount);
    }

    // empty state
    drawer.classList.toggle('is-empty', (cart.item_count || 0) === 0);

    // header badges
    const count = cart.item_count || 0;
    document.querySelectorAll('.cart-count').forEach((c) => { c.textContent = String(count); c.setAttribute('data-count', String(count)); });
    document.querySelectorAll('[data-cart-state]').forEach((s) => {
      s.setAttribute('data-count', String(count));
      s.textContent = count === 0 ? 'quiet.' : (count === 1 ? '1 waiting.' : count + ' waiting.');
    });

    // mark which of the six are already in the cart
    const handles = new Set((cart.items || []).map((it) => it.handle));
    drawer.querySelectorAll('[data-pair-add][data-product]').forEach((t) => {
      t.classList.toggle('is-added', handles.has(t.dataset.product));
    });
  }

  /* ---- cart API calls ---- */
  function fetchCart() {
    return fetch(cartJsUrl, { headers: { Accept: 'application/json' } })
      .then((r) => r.json()).then((cart) => { render(cart); return cart; })
      .catch(() => null);
  }
  function changeLine(key, quantity) {
    const prev = ((lastCart && lastCart.items) || []).find((i) => i.key === key);
    const name = prev ? prev.product_title : 'Item';
    return fetch(changeUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then((r) => r.json()).then((cart) => {
      // A throttled/conflicting change returns an error (no items[]); resync from the real cart.
      if (!cart || !Array.isArray(cart.items)) { setTimeout(fetchCart, 350); return cart; }
      render(cart);
      announce(quantity === 0 ? (name + ' removed from your bag.') : (name + ' quantity updated to ' + quantity + '.'));
      return cart;
    }).catch(() => { setTimeout(fetchCart, 350); return null; });
  }
  // Add via /cart/update.js: ONE round-trip that both updates the cart and returns
  // the full cart (totals + discount) — about half the latency of add.js + a re-fetch.
  function addVariant(id, quantity) {
    if (!id) return Promise.resolve(null);
    const want = quantity || 1;
    let current = 0;
    if (lastCart && Array.isArray(lastCart.items)) {
      lastCart.items.forEach((it) => { if (it.id === Number(id)) current += it.quantity; });
    }
    const target = Math.min(MAX_QTY, current + want); // never push a line past the cap
    const updates = {}; updates[id] = target;
    return fetch(updateUrl, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then((r) => {
      // On error, resync from the real cart so the drawer stays truthful, still open it.
      if (!r.ok) { return fetchCart().then(() => { open(); return null; }); }
      return r.json().then((cart) => {
        render(cart); open();
        const it = (cart.items || []).find((i) => i.id === Number(id));
        const cnt = cart.item_count || 0;
        announce((it ? it.product_title : 'Item') + ' added to your bag. ' + cnt + ' ' + itemWord(cnt) + ' total.');
        return cart;
      });
    }).catch(() => fetchCart().then(() => { open(); return null; }));
  }

  /* ---- open / close ---- */
  let lastFocus = null;
  // Hide the page behind the open drawer from assistive tech + Tab order.
  const bgEls = ['#MainContent', 'header.header', 'footer.footer']
    .map((s) => document.querySelector(s)).filter(Boolean);
  const setBgInert = (on) => bgEls.forEach((el) => { el.inert = on; });
  // Drawer thumbnails ship as data-drawer-src (speed audit 2026-11-04): a closed
  // drawer is only translated off-screen, so lazy <img src> still downloaded all six
  // bottles (~140KB) on EVERY page load, competing with the hero on slow phones.
  // They load on first open instead; warm them on first intent (hover/touch of a cart toggle).
  let thumbsLoaded = false;
  function loadThumbs() {
    if (thumbsLoaded) return;
    thumbsLoaded = true;
    drawer.querySelectorAll('img[data-drawer-src]').forEach((im) => { im.src = im.dataset.drawerSrc; im.removeAttribute('data-drawer-src'); });
  }
  document.querySelectorAll('[data-cart-open]').forEach((t) => {
    t.addEventListener('pointerenter', loadThumbs, { once: true, passive: true });
    t.addEventListener('touchstart', loadThumbs, { once: true, passive: true });
    t.addEventListener('focus', loadThumbs, { once: true });
  });
  function open() {
    loadThumbs();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    if (backdrop) backdrop.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-open]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
    document.documentElement.style.overflow = 'hidden';
    setBgInert(true);
    // Move focus into the dialog (the container, not the close button) so no
    // hard focus box lands on "close"; keyboard users still tab to controls.
    drawer.focus();
  }
  function close() {
    drawer.classList.remove('is-open');
    if (backdrop) backdrop.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-open]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
    document.documentElement.style.overflow = '';
    setBgInert(false); // lift inert BEFORE restoring focus — the cart toggle lives in the header
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- wiring ---- */
  document.querySelectorAll('[data-cart-open]').forEach((t) =>
    t.addEventListener('click', (e) => { e.preventDefault(); fetchCart(); open(); }));
  document.querySelectorAll('[data-cart-close]').forEach((c) =>
    c.addEventListener('click', close));
  document.addEventListener('keydown', (e) => {
    if (!drawer.classList.contains('is-open')) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key === 'Tab') {
      var f = Array.prototype.slice.call(drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      )).filter(function (el) { return el.offsetParent !== null; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // pair / empty-state tiles → add that fragrance (guard against double-add on fast taps)
  drawer.querySelectorAll('[data-pair-add]').forEach((tile) =>
    tile.addEventListener('click', () => {
      if (tile.dataset.busy) return;
      tile.dataset.busy = '1';
      Promise.resolve(addVariant(tile.dataset.variantId, 1)).finally(() => { delete tile.dataset.busy; });
    }));

  /* ---- per-line qty sync: optimistic UI + debounced server write ----
     Rapid +/- taps update the number AND the cap/button state instantly, and
     only the final quantity is sent to Shopify — so the drawer never feels
     stuck or laggy waiting on /cart/change (which throttles under bursts). ---- */
  const pendingQty = {};   // key -> latest intended quantity
  const syncTimers = {};   // key -> debounce timer
  function flushKey(key) {
    if (!(key in pendingQty)) return Promise.resolve(null);
    const q = pendingQty[key];
    delete pendingQty[key];
    clearTimeout(syncTimers[key]); delete syncTimers[key];
    return changeLine(key, q);
  }
  function flushAll() { return Promise.all(Object.keys(pendingQty).map(flushKey)); }
  function queueChange(key, quantity) {
    pendingQty[key] = quantity;
    clearTimeout(syncTimers[key]);
    syncTimers[key] = setTimeout(() => flushKey(key), 150);
  }
  // reflect a line's qty + cap state instantly, before the server confirms
  function reflectLine(line, qty) {
    const span = line.querySelector('.drawer-line-qty span');
    if (span) span.textContent = String(qty);
    const plus = line.querySelector('[data-qty-plus]');
    if (plus) { const atMax = qty >= MAX_QTY; plus.disabled = atMax; plus.classList.toggle('is-max', atMax); }
    const actions = line.querySelector('.drawer-line-actions');
    let note = line.querySelector('.drawer-line-max');
    if (qty >= MAX_QTY && !note && actions) {
      note = document.createElement('span');
      note.className = 'drawer-line-max'; note.textContent = 'Max ' + MAX_QTY;
      actions.insertBefore(note, actions.querySelector('[data-line-remove]'));
    } else if (qty < MAX_QTY && note) { note.remove(); }
  }

  // checkout — commit any pending qty changes first so checkout sees the real cart
  const checkoutBtn = drawer.querySelector('[data-checkout]');
  if (checkoutBtn) checkoutBtn.addEventListener('click', () => {
    const go = () => { window.location.href = '/checkout'; };
    if (!Object.keys(pendingQty).length) { go(); return; }
    flushAll().then(go);
  });

  // delegated qty / remove on line items
  body.addEventListener('click', (e) => {
    const line = e.target.closest('.drawer-line');
    if (!line) return;
    const key = line.dataset.key;
    const span = line.querySelector('.drawer-line-qty span');
    const qty = parseInt((span && span.textContent) || '1', 10);
    if (e.target.closest('[data-qty-minus]')) {
      const next = qty - 1;
      if (next <= 0) { line.style.display = 'none'; queueChange(key, 0); return; } // remove, no "0" flash
      reflectLine(line, next);
      queueChange(key, next);
    } else if (e.target.closest('[data-qty-plus]')) {
      if (qty >= MAX_QTY) return;                            // per-line cap
      reflectLine(line, qty + 1);
      queueChange(key, qty + 1);
    } else if (e.target.closest('[data-line-remove]')) {
      line.style.display = 'none';                           // instant feedback
      queueChange(key, 0);
    }
  });

  window.PlatonicCart = { open, close, refresh: fetchCart, addVariant, render };

  // hydrate on load (cart may already have items)
  fetchCart();
})();
